/**
 * Best-effort, client-side check for "is this visitor in the EU / EEA / UK?",
 * used to decide whether to surface the cookie-consent banner at all.
 *
 * There is no CDN / Front Door in front of the Container App, so no geo
 * header reaches the server (see plan / ADR notes). Rather than add an IP
 * geolocation dependency and process client IPs, we read the browser's
 * IANA timezone — it requires no network, no dependency, and no PII.
 *
 * Trade-off: a timezone is a coarse proxy. We deliberately err toward
 * *over*-showing: every `Europe/*` zone counts (which sweeps in a few
 * non-EU Europeans — Switzerland, Norway, the UK, even Moscow), plus the
 * handful of EEA zones that live under `Atlantic/*` or `Asia/*`. The cost
 * of a false positive is one extra banner for a European visitor (safe,
 * arguably correct). The thing we must NOT do is show it to everyone — a
 * US / AU / APAC visitor resolves to `America/*`, `Australia/*`, `Asia/*`
 * (non-Cyprus) etc. and is correctly skipped.
 */

// EEA/EU member-state zones that are NOT under the `Europe/` prefix.
const NON_EUROPE_PREFIX_ZONES = new Set<string>([
  "Atlantic/Reykjavik", // Iceland (EEA)
  "Atlantic/Canary", // Spain — Canary Islands
  "Atlantic/Azores", // Portugal — Azores
  "Atlantic/Madeira", // Portugal — Madeira
  "Asia/Nicosia", // Cyprus
  "Asia/Famagusta", // Cyprus (Northern)
]);

/** True when the visitor's browser timezone suggests they're in the
 *  EU / EEA / UK (or close enough that showing the banner is the safe
 *  call). Defaults to `false` only when the timezone can't be read. */
export function isLikelyEU(): boolean {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Intl/timezone unavailable — fail open to "not EU" rather than
    // showing the banner globally (the explicit product requirement is
    // EU-only, not everyone).
    return false;
  }
  if (!tz) return false;
  return tz.startsWith("Europe/") || NON_EUROPE_PREFIX_ZONES.has(tz);
}
